export const NAVIGATOR_CHANNEL = 'empower.pathway.navigator'
export const NAVIGATOR_PROTOCOL_VERSION = 1
export const DEFAULT_NAVIGATOR_ROUTE = 'schedule'

const PATHWAY_NAVIGATOR_PREFIX = '/scheduling'
const MAX_ROUTE_LENGTH = 256
const MAX_GUIDE_SEGMENTS = 6
const MAX_IDENTIFIER_LENGTH = 160
const GUIDE_SLUG_SEGMENT = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
const RELEASE_SLUG_SEGMENT = /^[a-z0-9]+(?:[.-][a-z0-9]+)*$/
const SAFE_IDENTIFIER = /^[A-Za-z0-9._:-]+$/
const SAFE_EMAIL_LOCAL_PART = /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+$/
const SAFE_EMAIL_DOMAIN_LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/
const EXPECTED_USER_HASH = /^[a-f0-9]{64}$/

const STATIC_NAVIGATOR_ROUTES = new Set([
  'schedule',
  'callouts',
  'utilization',
  'history',
  'sync',
  'guide',
  'users',
  'clinics',
  'rules',
  'settings',
  'releases',
])

const UTILIZATION_CHILD_ROUTES = new Set([
  'leaderboard',
  'callouts',
  'overtime',
  'credits',
  'summary',
  'patients',
  'undercoverage',
])

const RULE_CHILD_ROUTES = new Set([
  'attendance',
  'replacements',
  'credits',
  'scheduling',
  'staff-targets',
])

const ROUTE_ALIASES = Object.freeze({
  approvals: 'callouts',
  'chatbot-guide': 'guide/integrations/teams-agent-overview',
})

export const NAVIGATOR_MESSAGE_TYPES = Object.freeze([
  'navigator.ready',
  'navigator.route.changed',
  'navigator.navigation.applied',
  'navigator.navigation.rejected',
  'navigator.auth.required',
  'navigator.signed_out',
])

export const PATHWAY_MESSAGE_TYPES = Object.freeze([
  'pathway.initialize',
  'pathway.navigate',
  'pathway.theme.set',
  'pathway.sign_out',
])

const NAVIGATOR_MESSAGE_TYPE_SET = new Set(NAVIGATOR_MESSAGE_TYPES)
const PATHWAY_MESSAGE_TYPE_SET = new Set(PATHWAY_MESSAGE_TYPES)
const ALL_MESSAGE_TYPE_SET = new Set([
  ...NAVIGATOR_MESSAGE_TYPES,
  ...PATHWAY_MESSAGE_TYPES,
])

const ROUTE_PAYLOAD_TYPES = new Set([
  'navigator.ready',
  'navigator.route.changed',
  'navigator.navigation.applied',
  'navigator.navigation.rejected',
  'navigator.auth.required',
  'pathway.navigate',
])

const EMPTY_PAYLOAD_TYPES = new Set([
  'navigator.signed_out',
  'pathway.sign_out',
])

const THEME_PAYLOAD_TYPES = new Set([
  'pathway.theme.set',
])

const SEQUENCE_REQUIRED_TYPES = new Set([
  'navigator.ready',
  'navigator.route.changed',
])

const REQUEST_REQUIRED_TYPES = new Set([
  'navigator.navigation.applied',
  'navigator.navigation.rejected',
  'navigator.signed_out',
  'pathway.initialize',
  'pathway.navigate',
  'pathway.sign_out',
])

const ENVELOPE_KEYS = new Set([
  'channel',
  'version',
  'type',
  'instanceId',
  'seq',
  'requestId',
  'payload',
])

function isPlainObject(value) {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }

  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

function containsAsciiControlCharacter(value) {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index)
    if (code <= 0x1F || code === 0x7F) return true
  }
  return false
}

function isSafeIdentifier(value) {
  return typeof value === 'string'
    && value.length > 0
    && value.length <= MAX_IDENTIFIER_LENGTH
    && SAFE_IDENTIFIER.test(value)
}

function isAllowedNavigatorRoute(route) {
  if (STATIC_NAVIGATOR_ROUTES.has(route)) {
    return true
  }

  const segments = route.split('/')
  const [section, child, third] = segments

  if (section === 'rules') {
    return segments.length === 2 && RULE_CHILD_ROUTES.has(child)
  }

  if (section === 'utilization') {
    return segments.length === 2 && UTILIZATION_CHILD_ROUTES.has(child)
  }

  if (section === 'guide') {
    const guideSegments = segments.slice(1)
    return guideSegments.length > 0
      && guideSegments.length <= MAX_GUIDE_SEGMENTS
      && guideSegments.every((segment) => GUIDE_SLUG_SEGMENT.test(segment))
  }

  if (section === 'releases') {
    if (segments.length === 2) {
      return RELEASE_SLUG_SEGMENT.test(child)
    }

    return segments.length === 3
      && RELEASE_SLUG_SEGMENT.test(child)
      && third === 'presenter'
  }

  return false
}

/**
 * Converts a route-like value into the canonical Navigator route key used by
 * the Pathway bridge. The returned value never contains a leading slash,
 * query string, hash, origin, or unsafe path segment.
 */
export function normalizeNavigatorRoute(value) {
  if (typeof value !== 'string') {
    return null
  }

  if (!value || value.length > MAX_ROUTE_LENGTH) {
    return null
  }

  if (
    value.startsWith('/')
    || value.endsWith('/')
    || value.includes('//')
    || /[?#%\\\s]/.test(value)
    || containsAsciiControlCharacter(value)
  ) {
    return null
  }

  const aliasedRoute = Object.prototype.hasOwnProperty.call(ROUTE_ALIASES, value)
    ? ROUTE_ALIASES[value]
    : value
  const segments = aliasedRoute.split('/')
  if (segments.some((segment) => segment === '.' || segment === '..')) {
    return null
  }

  return isAllowedNavigatorRoute(aliasedRoute) ? aliasedRoute : null
}

export function navigatorRouteFromPathwayPath(pathname) {
  if (typeof pathname !== 'string' || pathname.includes('?') || pathname.includes('#')) {
    return null
  }

  if (pathname === PATHWAY_NAVIGATOR_PREFIX || pathname === `${PATHWAY_NAVIGATOR_PREFIX}/`) {
    return DEFAULT_NAVIGATOR_ROUTE
  }

  if (!pathname.startsWith(`${PATHWAY_NAVIGATOR_PREFIX}/`)) {
    return null
  }

  const route = pathname.slice(PATHWAY_NAVIGATOR_PREFIX.length + 1)
  if (!route || route.startsWith('/')) {
    return null
  }

  return normalizeNavigatorRoute(route)
}

/** True for the reserved Pathway integration namespace, with a path boundary. */
export function isNavigatorPathwayPath(pathname) {
  return typeof pathname === 'string'
    && (pathname === PATHWAY_NAVIGATOR_PREFIX
      || pathname.startsWith(`${PATHWAY_NAVIGATOR_PREFIX}/`))
}

export function pathwayPathForNavigatorRoute(route) {
  const normalizedRoute = normalizeNavigatorRoute(route)
  return normalizedRoute ? `${PATHWAY_NAVIGATOR_PREFIX}/${normalizedRoute}` : null
}

export function navigatorPathForRoute(route) {
  const normalizedRoute = normalizeNavigatorRoute(route)
  return normalizedRoute ? `/${normalizedRoute}` : null
}

/**
 * Builds the top-level Navigator launch URL without putting the deterministic
 * user fingerprint in the HTTP request target. Navigator consumes and scrubs
 * the fragment before preserving the binding in sessionStorage.
 */
export function createNavigatorLaunchUrl({
  navigatorOrigin,
  pathwayOrigin,
  route,
  expectedUserHash,
}) {
  const normalizedNavigatorOrigin = normalizeNavigatorOrigin(navigatorOrigin)
  const normalizedPathwayOrigin = normalizeNavigatorOrigin(pathwayOrigin)
  const navigatorPath = navigatorPathForRoute(route)
  const pathwayPath = pathwayPathForNavigatorRoute(route)

  if (
    !normalizedNavigatorOrigin
    || !normalizedPathwayOrigin
    || !navigatorPath
    || !pathwayPath
    || !isNavigatorExpectedUserHash(expectedUserHash)
  ) {
    return null
  }

  const launchUrl = new URL('/auth/pathway-launch', normalizedNavigatorOrigin)
  launchUrl.searchParams.set('next', navigatorPath)
  launchUrl.searchParams.set(
    'returnTo',
    new URL(pathwayPath, normalizedPathwayOrigin).toString(),
  )
  launchUrl.hash = new URLSearchParams({ expectedUserHash }).toString()
  return launchUrl.toString()
}

/**
 * Returns a normalized origin or null. Production Navigator origins must use
 * HTTPS; HTTP is accepted only for loopback development hosts.
 */
export function normalizeNavigatorOrigin(raw) {
  if (typeof raw !== 'string' || !raw.trim()) {
    return null
  }

  try {
    const parsed = new URL(raw.trim())
    const isLoopback = ['localhost', '127.0.0.1', '[::1]'].includes(parsed.hostname)
    const hasOriginOnlyPath = parsed.pathname === '' || parsed.pathname === '/'

    if (
      (parsed.protocol !== 'https:' && !(parsed.protocol === 'http:' && isLoopback))
      || parsed.username
      || parsed.password
      || parsed.search
      || parsed.hash
      || !hasOriginOnlyPath
      || parsed.origin === 'null'
    ) {
      return null
    }

    return parsed.origin
  } catch (error) {
    return null
  }
}

/**
 * Canonical user identity shared by the two authenticated applications before
 * hashing. The bridge intentionally supports ASCII email/UPN values only so
 * both runtimes produce the same UTF-8 bytes without locale-dependent rules.
 */
export function normalizeNavigatorUserEmail(value) {
  if (typeof value !== 'string') {
    return null
  }

  const normalized = value.trim().toLowerCase()
  if (!normalized || normalized.length > 254 || /[^\x21-\x7E]/.test(normalized)) {
    return null
  }

  const parts = normalized.split('@')
  if (parts.length !== 2) {
    return null
  }

  const [localPart, domain] = parts
  const domainLabels = domain.split('.')
  if (
    !localPart
    || localPart.length > 64
    || !SAFE_EMAIL_LOCAL_PART.test(localPart)
    || domainLabels.some((label) => !SAFE_EMAIL_DOMAIN_LABEL.test(label))
  ) {
    return null
  }

  return normalized
}

export function isNavigatorExpectedUserHash(value) {
  return typeof value === 'string' && EXPECTED_USER_HASH.test(value)
}

/**
 * Creates the deterministic identity fingerprint sent during initialize.
 * It detects accidental account mismatch; it is not an authorization proof.
 * No raw email, access token, role, or Supabase identifier crosses the frame.
 */
export async function createNavigatorExpectedUserHash(value, subtleCrypto) {
  const normalized = normalizeNavigatorUserEmail(value)
  if (!normalized) {
    throw new TypeError('Navigator expected user email is missing or invalid')
  }

  const digestProvider = subtleCrypto
    || (typeof window !== 'undefined' && window.crypto
      ? window.crypto.subtle
      : null)

  if (!digestProvider || typeof digestProvider.digest !== 'function') {
    throw new TypeError('SHA-256 is unavailable for Navigator identity matching')
  }

  // normalizeNavigatorUserEmail permits ASCII only, whose code points are the
  // exact UTF-8 byte representation required by the cross-application contract.
  const input = Uint8Array.from(normalized, (character) => character.charCodeAt(0))
  const digest = await digestProvider.digest('SHA-256', input)
  const bytes = new Uint8Array(digest)

  if (bytes.length !== 32) {
    throw new TypeError('SHA-256 returned an invalid Navigator identity digest')
  }

  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

function hasValidEnvelopeShape(data, allowedTypes) {
  if (!isPlainObject(data)) {
    return false
  }

  if (Object.keys(data).some((key) => !ENVELOPE_KEYS.has(key))) {
    return false
  }

  if (
    data.channel !== NAVIGATOR_CHANNEL
    || data.version !== NAVIGATOR_PROTOCOL_VERSION
    || !allowedTypes.has(data.type)
    || !isSafeIdentifier(data.instanceId)
  ) {
    return false
  }

  if (data.seq !== undefined && (!Number.isSafeInteger(data.seq) || data.seq < 0)) {
    return false
  }

  if (SEQUENCE_REQUIRED_TYPES.has(data.type) && data.seq === undefined) {
    return false
  }
  if (!SEQUENCE_REQUIRED_TYPES.has(data.type) && data.seq !== undefined) {
    return false
  }

  if (data.requestId !== undefined && !isSafeIdentifier(data.requestId)) {
    return false
  }

  if (REQUEST_REQUIRED_TYPES.has(data.type) && data.requestId === undefined) {
    return false
  }
  if (!REQUEST_REQUIRED_TYPES.has(data.type) && data.requestId !== undefined) {
    return false
  }

  if (data.type === 'pathway.initialize') {
    if (!isPlainObject(data.payload) || Object.keys(data.payload).length !== 2) {
      return false
    }

    const normalizedRoute = normalizeNavigatorRoute(data.payload.route)
    return normalizedRoute !== null
      && normalizedRoute === data.payload.route
      && isNavigatorExpectedUserHash(data.payload.expectedUserHash)
  }

  if (ROUTE_PAYLOAD_TYPES.has(data.type)) {
    if (!isPlainObject(data.payload) || Object.keys(data.payload).length !== 1) {
      return false
    }

    const normalizedRoute = normalizeNavigatorRoute(data.payload.route)
    return normalizedRoute !== null && normalizedRoute === data.payload.route
  }

  if (THEME_PAYLOAD_TYPES.has(data.type)) {
    return isPlainObject(data.payload)
      && Object.keys(data.payload).length === 1
      && (data.payload.theme === 'light' || data.payload.theme === 'dark')
  }

  if (EMPTY_PAYLOAD_TYPES.has(data.type)) {
    return data.payload === undefined
      || (isPlainObject(data.payload) && Object.keys(data.payload).length === 0)
  }

  return false
}

export function isNavigatorEnvelope(data) {
  return hasValidEnvelopeShape(data, NAVIGATOR_MESSAGE_TYPE_SET)
}

export function isPathwayEnvelope(data) {
  return hasValidEnvelopeShape(data, PATHWAY_MESSAGE_TYPE_SET)
}

export function createNavigatorMessage({
  type,
  instanceId,
  seq,
  requestId,
  payload,
}) {
  if (!ALL_MESSAGE_TYPE_SET.has(type)) {
    throw new TypeError(`Unknown Navigator bridge message type: ${type}`)
  }

  const message = {
    channel: NAVIGATOR_CHANNEL,
    version: NAVIGATOR_PROTOCOL_VERSION,
    type,
    instanceId,
  }

  if (seq !== undefined) {
    message.seq = seq
  }
  if (requestId !== undefined) {
    message.requestId = requestId
  }
  if (payload !== undefined) {
    message.payload = isPlainObject(payload)
      ? Object.freeze({ ...payload })
      : payload
  }

  const allowedTypes = NAVIGATOR_MESSAGE_TYPE_SET.has(type)
    ? NAVIGATOR_MESSAGE_TYPE_SET
    : PATHWAY_MESSAGE_TYPE_SET

  if (!hasValidEnvelopeShape(message, allowedTypes)) {
    throw new TypeError('Invalid Navigator bridge message')
  }

  return Object.freeze(message)
}

export function createNavigatorRequestId(randomUuidFactory) {
  const factory = randomUuidFactory
    || (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? () => crypto.randomUUID()
      : null)

  const randomPart = factory
    ? factory()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  const requestId = `pathway-${randomPart}`

  if (!isSafeIdentifier(requestId)) {
    throw new TypeError('Navigator request ID factory returned an unsafe value')
  }

  return requestId
}

export function nextNavigatorSequence(currentSequence = 0) {
  if (!Number.isSafeInteger(currentSequence) || currentSequence < 0) {
    throw new TypeError('Navigator sequence must be a non-negative safe integer')
  }

  if (currentSequence === Number.MAX_SAFE_INTEGER) {
    throw new RangeError('Navigator sequence exhausted for the current iframe instance')
  }

  return currentSequence + 1
}

export function isNewerNavigatorSequence(incomingSequence, lastSequence) {
  return Number.isSafeInteger(incomingSequence)
    && incomingSequence >= 0
    && Number.isSafeInteger(lastSequence)
    && lastSequence >= 0
    && incomingSequence > lastSequence
}

export function isTrustedNavigatorMessageEvent(event, expectedOrigin, expectedSource) {
  const normalizedOrigin = normalizeNavigatorOrigin(expectedOrigin)
  return Boolean(
    event
    && normalizedOrigin
    && event.origin === normalizedOrigin
    && event.source === expectedSource
    && isNavigatorEnvelope(event.data)
  )
}

export function isCurrentNavigatorInstance(message, instanceId) {
  return Boolean(message && isSafeIdentifier(instanceId) && message.instanceId === instanceId)
}
